"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Bot, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Card, CardContent } from "../components/ui/card";
import { useAssistantAvatar } from "../contexts/AssistantAvatarContext";
import { useAuth } from "@workos-inc/authkit-react";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";

export default function Home() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const {
    assistants,
    currentAssistant,
    setCurrentAssistant,
    isLoadingPhoto,
  } = useAssistantAvatar();
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  const [isSelecting, setIsSelecting] = useState<number | null>(null);

  // Use isLoadingPhoto for initial load, isLoading for actions
  const isInitialLoading = isLoadingPhoto;

  // Auto-skip home page if only one assistant exists
  useEffect(() => {
    if (!isInitialLoading && assistants.length === 1) {
      // Set the only assistant as current and redirect
      const onlyAssistant = assistants[0];
      if (currentAssistant?.id !== onlyAssistant.id) {
        setCurrentAssistant(onlyAssistant.id).then(() => {
          navigateToNextPage();
        });
      } else {
        navigateToNextPage();
      }
    }
  }, [isInitialLoading, assistants, currentAssistant]);

  const navigateToNextPage = () => {
    if (role === "admin" || role === "superadmin") {
      navigate("/documents", { replace: true });
    } else {
      navigate("/assistant", { replace: true });
    }
  };

  const handleSelectAssistant = async (assistantId: number) => {
    setIsSelecting(assistantId);
    try {
      await setCurrentAssistant(assistantId);
      navigateToNextPage();
    } catch (error) {
      console.error("Failed to select assistant:", error);
      setIsSelecting(null);
    }
  };

  // Show loading while checking assistants
  if (isInitialLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex space-x-2">
          <div
            className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.3s]"
            style={{ backgroundColor: APP_COLOR }}
          />
          <div
            className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.15s]"
            style={{ backgroundColor: APP_COLOR }}
          />
          <div
            className="w-2 h-2 rounded-full animate-bounce"
            style={{ backgroundColor: APP_COLOR }}
          />
        </div>
      </div>
    );
  }

  // If only one assistant, this will redirect (handled in useEffect)
  if (assistants.length === 1) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex space-x-2">
          <div
            className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.3s]"
            style={{ backgroundColor: APP_COLOR }}
          />
          <div
            className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.15s]"
            style={{ backgroundColor: APP_COLOR }}
          />
          <div
            className="w-2 h-2 rounded-full animate-bounce"
            style={{ backgroundColor: APP_COLOR }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Willkommen
            </h1>
            <p className="text-lg text-slate-500">
              Wählen Sie einen Assistenten aus, um fortzufahren
            </p>
          </div>

          {/* Assistants Grid */}
          <div className="grid gap-4 md:grid-cols-2">
            {assistants.map((assistant) => {
              const isCurrentlySelecting = isSelecting === assistant.id;

              return (
                <Card
                  key={assistant.id}
                  className="relative overflow-hidden transition-all cursor-pointer hover:shadow-lg"
                  onClick={() => !isSelecting && handleSelectAssistant(assistant.id)}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center gap-3">
                      {/* Avatar */}
                      <Avatar
                        className="h-16 w-16"
                        style={{ boxShadow: "0 0 0 2px #e2e8f0" }}
                      >
                        <AvatarImage
                          src={assistant.photo_url || undefined}
                          alt={assistant.name}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-gradient-to-br from-slate-100 to-slate-200">
                          <Bot className="h-8 w-8 text-slate-400" />
                        </AvatarFallback>
                      </Avatar>

                      {/* Name */}
                      <h3 className="text-lg font-semibold text-slate-900 text-center">
                        {assistant.name}
                      </h3>

                      {/* Description */}
                      {assistant.description && (
                        <p className="text-sm text-slate-500 text-center line-clamp-2">
                          {assistant.description}
                        </p>
                      )}

                      {/* SharePoint Folder - only show if SharePoint is enabled and folder is set */}
                      {appConfig.features.sharePoint && assistant.sharepoint_root_folder && (
                        <p className="text-xs text-slate-500 text-center truncate max-w-full">
                          📁 {assistant.sharepoint_root_folder}
                        </p>
                      )}
                    </div>

                    {/* Select Button */}
                    <div className="mt-4">
                      <button
                        disabled={isSelecting !== null}
                        className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium transition-colors text-white h-10 px-4 disabled:opacity-50"
                        style={{ backgroundColor: APP_COLOR }}
                      >
                        {isCurrentlySelecting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Wird ausgewählt...
                          </>
                        ) : (
                          "Auswählen"
                        )}
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Empty State - Only show after loading completes */}
          {!isInitialLoading && assistants.length === 0 && (
            <div className="text-center py-12">
              <div
                className="inline-flex p-4 rounded-full mb-4"
                style={{ backgroundColor: `${APP_COLOR}15` }}
              >
                <Bot className="h-12 w-12" style={{ color: APP_COLOR }} />
              </div>
              <h3 className="text-xl font-medium text-slate-700 mb-2">
                Keine Assistenten verfügbar
              </h3>
              <p className="text-sm text-slate-500">
                Bitte kontaktieren Sie einen Administrator.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
